import { useTranslation } from "@sagot/i18n";
import { Field, Input, Select, Switch, Textarea } from "@sagot/ui";
import { IntegerInput } from "../../../workspace/shared";
import { OptionSelect } from "../builder/action-forms";
import type { FlowContext } from "../builder/flow-context";
import { useRefs } from "../builder/refs";
import type { FormProps } from "../builder/webhook-form";
import { RESOLVING_STATUS_CATEGORIES, SERVICE_STEP_LIMITS, STEP_PRIORITIES, type TicketPriority } from "./service-constants";
import { AnswersField, ServiceExits, ServiceVariables, SettingsLink, TagsField, TypeField } from "./service-fields";
import type { CreateTicketStep, RouteTicketStep, SetTicketStateStep, UpdateTicketStep } from "./service-types";

const F = "engage.workflows.service.fields";
const MINUTES_PER_DAY = 24 * 60;

type Flow = Pick<FlowContext, "paths" | "canAddPath" | "facing">;

function PrioritySelect({ value, label, emptyLabel, disabled, onChange }: { value: TicketPriority | undefined; label: string; emptyLabel: string; disabled: boolean; onChange: (priority: TicketPriority | undefined) => void }) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      <Select value={value ?? ""} disabled={disabled} onChange={(e) => { onChange(e.target.value === "" ? undefined : (e.target.value as TicketPriority)); }}>
        <option value="">{emptyLabel}</option>
        {STEP_PRIORITIES.map((priority) => <option key={priority} value={priority}>{t(`engage.workflows.service.priorities.${priority}`)}</option>)}
      </Select>
    </Field>
  );
}

/** Create ticket: the type, the words, who gets it first, and what the steps after it can use (`var.ticket_number`). */
export function CreateTicketForm({ step, disabled, onChange, ...flow }: FormProps<CreateTicketStep> & Flow) {
  const { t } = useTranslation();
  const { teams } = useRefs();
  return (
    <div className="space-y-5">
      <TypeField step={step} disabled={disabled} label={t(`${F}.ticketType`)} hint={t(`${F}.createTypeHint`)} onChange={(next) => { onChange({ ...step, type_id: next.type_id, type_key: next.type_key }); }} />
      <Field label={t(`${F}.ticketTitle`)} hint={t(`${F}.ticketTitleHint`)}>
        <Input maxLength={SERVICE_STEP_LIMITS.title} disabled={disabled} value={step.title} onChange={(e) => { onChange({ ...step, title: e.target.value }); }} />
      </Field>
      <div>
        <Field label={t(`${F}.ticketDescription`)}>
          <Textarea rows={4} maxLength={SERVICE_STEP_LIMITS.text} disabled={disabled} value={step.description ?? ""} onChange={(e) => { onChange({ ...step, description: e.target.value }); }} />
        </Field>
        <ServiceVariables />
      </div>
      <PrioritySelect value={step.priority} label={t(`${F}.priority`)} emptyLabel={t(`${F}.priorityTypeDefault`)} disabled={disabled} onChange={(priority) => { onChange({ ...step, priority }); }} />
      <OptionSelect label={t(`${F}.team`)} value={step.team_id} options={teams} disabled={disabled} empty={t(`${F}.teamTypeDefault`)} onChange={(team_id) => { onChange({ ...step, team_id }); }} />
      <TagsField label={t(`${F}.tags`)} hint={t(`${F}.tagsHint`)} value={step.tags ?? []} disabled={disabled} onChange={(tags) => { onChange({ ...step, tags: tags.length > 0 ? tags : undefined }); }} />
      <AnswersField value={step.fields} disabled={disabled} onChange={(fields) => { onChange({ ...step, fields }); }} />
      <div className="flex items-center justify-between gap-4 rounded-md bg-surface-2 px-3 py-2.5">
        <span id={`${step.id}-notify`} className="text-sm font-semibold">{t(`${F}.notifyClient`)}</span>
        <Switch checked={step.notify_client !== false} disabled={disabled} onCheckedChange={(notify_client) => { onChange({ ...step, notify_client }); }} aria-labelledby={`${step.id}-notify`} />
      </div>
      <p className="text-xs text-fg-subtle">{t(`${F}.createVariables`, { number: "{{var.ticket_number}}", id: "{{var.ticket_id}}" })}</p>
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

type DueMode = "keep" | "set" | "clear";
const dueModeOf = (step: UpdateTicketStep): DueMode => (step.clear_due ? "clear" : step.due_in_minutes ? "set" : "keep");

function DueField({ step, disabled, onChange }: FormProps<UpdateTicketStep>) {
  const { t } = useTranslation();
  const mode = dueModeOf(step);
  const set = (next: DueMode) => {
    if (next === "keep") onChange({ ...step, due_in_minutes: undefined, clear_due: undefined });
    else if (next === "clear") onChange({ ...step, due_in_minutes: undefined, clear_due: true });
    else onChange({ ...step, due_in_minutes: step.due_in_minutes ?? MINUTES_PER_DAY, clear_due: undefined });
  };
  return (
    <div className="space-y-2">
      <Field label={t(`${F}.due`)}>
        <Select value={mode} disabled={disabled} onChange={(e) => { set(e.target.value as DueMode); }}>
          {(["keep", "set", "clear"] as const).map((value) => <option key={value} value={value}>{t(`${F}.dueModes.${value}`)}</option>)}
        </Select>
      </Field>
      {mode === "set" ? (
        <Field label={t(`${F}.dueIn`)} hint={t(`${F}.dueInHint`)}>
          <IntegerInput min={1} max={SERVICE_STEP_LIMITS.dueInMinutesMax} disabled={disabled} value={step.due_in_minutes ?? MINUTES_PER_DAY} onChange={(due_in_minutes) => { onChange({ ...step, due_in_minutes }); }} />
        </Field>
      ) : null}
    </div>
  );
}

/** Update ticket: change only what is chosen here; everything left alone stays as it is. */
export function UpdateTicketForm({ step, disabled, onChange, ...flow }: FormProps<UpdateTicketStep> & Flow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-xs text-fg-muted">{t(`${F}.updateIntro`)}</p>
      <TypeField step={step} disabled={disabled} emptyLabel={t(`${F}.typeKeep`)} label={t(`${F}.ticketType`)} onChange={(next) => { onChange({ ...step, type_id: next.type_id, type_key: next.type_key }); }} />
      <PrioritySelect value={step.priority} label={t(`${F}.priority`)} emptyLabel={t(`${F}.priorityKeep`)} disabled={disabled} onChange={(priority) => { onChange({ ...step, priority }); }} />
      <TagsField label={t(`${F}.tagsAdd`)} hint={t(`${F}.tagsHint`)} value={step.tags_add ?? []} disabled={disabled} onChange={(tags) => { onChange({ ...step, tags_add: tags.length > 0 ? tags : undefined }); }} />
      <TagsField label={t(`${F}.tagsRemove`)} hint={t(`${F}.tagsHint`)} value={step.tags_remove ?? []} disabled={disabled} onChange={(tags) => { onChange({ ...step, tags_remove: tags.length > 0 ? tags : undefined }); }} />
      <AnswersField value={step.fields} disabled={disabled} onChange={(fields) => { onChange({ ...step, fields }); }} />
      <DueField step={step} disabled={disabled} onChange={onChange} />
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

/** Set ticket state: the status to move to; a resolving move can carry the resolution code and a note. */
export function SetTicketStateForm({ step, disabled, onChange, ...flow }: FormProps<SetTicketStateStep> & Flow) {
  const { t } = useTranslation();
  const { service } = useRefs();
  const statuses = service?.statuses ?? [];
  const status = statuses.find((candidate) => candidate.id === step.status);
  const resolving = status !== undefined && RESOLVING_STATUS_CATEGORIES.has(status.category);
  const codes = (service?.codes ?? []).filter((code) => status === undefined || code.categories.includes(status.category));
  return (
    <div className="space-y-5">
      <Field label={t(`${F}.status`)} hint={t(`${F}.statusHint`)}>
        <Select
          value={step.status}
          disabled={disabled}
          onChange={(e) => { onChange({ ...step, status: e.target.value, resolution_code: undefined, resolution_note: undefined }); }}
        >
          <option value="">{t(`${F}.choose`)}</option>
          {step.status && !status ? <option value={step.status}>{step.status}</option> : null}
          {statuses.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}
        </Select>
      </Field>
      {statuses.length === 0 ? <p className="text-xs text-fg-muted">{t(`${F}.noStatuses`)} <SettingsLink label={t(`${F}.openSettings`)} tab="statuses" /></p> : null}
      {resolving ? (
        <>
          <OptionSelect label={t(`${F}.resolutionCode`)} value={step.resolution_code} options={codes} disabled={disabled} empty={t(`${F}.noCode`)} onChange={(resolution_code) => { onChange({ ...step, resolution_code }); }} />
          <Field label={t(`${F}.resolutionNote`)} hint={t(`${F}.resolutionNoteHint`)}>
            <Textarea rows={2} maxLength={SERVICE_STEP_LIMITS.resolutionNote} disabled={disabled} value={step.resolution_note ?? ""} onChange={(e) => { onChange({ ...step, resolution_note: e.target.value || undefined }); }} />
          </Field>
        </>
      ) : null}
      <p className="text-xs text-fg-subtle">{t(`${F}.stateMachine`)}</p>
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

/** Route ticket: nothing to set, the workspace's own routing rules decide; the exits say whether a person or a queue got it. */
export function RouteTicketForm({ step, disabled, onChange, ...flow }: FormProps<RouteTicketStep> & Flow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.routeHelp`)}</p>
      <p className="text-xs text-fg-muted"><SettingsLink label={t(`${F}.openRouting`)} /></p>
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}
